import { of,  Subject, BehaviorSubject } from 'rxjs';
import { Component, OnDestroy, OnInit, ViewChildren, QueryList, ElementRef } from '@angular/core';
import { StorageService, UserService, ItemService, TenantService } from '../services'
import { ActivatedRoute, Router, NavigationEnd, UrlSegment } from '@angular/router';
import * as moment from "moment";
import { takeUntil, filter, flatMap, tap, map } from 'rxjs/operators';
import { ItemSummary } from '../summary/itemSummary.component';
import * as _ from 'lodash';
import { MatDialog } from '@angular/material/dialog';
import { TenantSuspendedDialogComponent } from '../general/tenantSuspendedDialog.component';
import { ItemContextModel } from '../model';

@Component({
    selector: 'recent-pane',
    styles: [`
        mat-card {
            margin-bottom: 6px;
        }
        
        .title {
            font-size: 20px;
        }
        
        .card-container {
            height: 100%
        }
        
        .title-item {
            display: inline-block;
        }

        .itemsPanel {
            overflow-y: auto;
            height: calc(100vh - 145px);
        }
    `],
    template: `
        <div fxLayout="column" *ngIf="!showItem" class="card-container">
            <mat-card>
                <mat-card-content>
                    <mat-card-title class="title">
                        <div class="title-item">Recent Updates</div>
                        <mat-chip-list *ngIf="totalItems" class="title-item" style="padding-left: 20px;"><mat-chip>{{totalItems}}</mat-chip></mat-chip-list>
                    </mat-card-title>
                    <span fxFlex></span>
                    <button mat-button 
                            [matTooltip]="'Sorted by ' + (searchParams.value?.sortField === 'publishedDate' ? 'Published Date' : 'Attention Rank')"
                            color="primary" 
                            [matMenuTriggerFor]="menu"><mat-icon>filter_list</mat-icon></button>
                    <mat-menu #menu="matMenu">
                        <button mat-menu-item 
                                matTooltip="by Published Date"
                                [disabled]="searchParams.value?.sortField === 'publishedDate'"
                                (click)="sort('publishedDate')"><mat-icon>schedule</mat-icon>Time</button>
                        <button mat-menu-item
                                matTooltip="by Attention Rank"
                                [disabled]="searchParams.value?.sortField === 'attentionRank'" 
                                (click)="sort('attentionRank')"><mat-icon>photo_filter</mat-icon>Rank</button>
                    </mat-menu>
                    <button mat-button color="primary" matTooltip="Mark all as read" (click)="markNow()"><mat-icon>done_all</mat-icon></button>
                </mat-card-content>
            </mat-card>
            <div *ngIf="items | async as itemContents">
                <div *ngIf="!noTouching && itemContents && !itemContents.length" fxLayout="column" fxLayoutAlign="center center">
                    <h1 class="text-center"><mat-icon>done_all</mat-icon></h1>
                    <div style="font-size: 20px;">You're up to date.</div>
                </div>
                <div *ngIf="!noTouching && itemContents && itemContents.length" 
                     class="itemsPanel"
                     infiniteScroll 
                     [infiniteScrollThrottle]="300"
                     [infiniteScrollDistance]="1"
                     [scrollWindow]="false"
                     (scrolled)="retrievePage()">
                    <span *ngFor="let item of itemContents">
                        <item-summary [item]="item" [attr.itemId]="item.id"></item-summary>
                    </span>
                    <div *ngIf="pagingNoTouching" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 25px;">
                        <spinner></spinner>
                    </div>        
                </div>
            </div>
            <div *ngIf="noTouching" fxLayout="row" fxLayoutAlign="center center" style="padding-top: 25px;">
                <spinner></spinner>
            </div>
        </div>
    `})
export class RecentPane implements OnInit, OnDestroy {
    cleanupTime = new Subject<void>();
    searchParams = new BehaviorSubject(null);
    items = new BehaviorSubject([]);
    totalItems: number;
    noTouching: boolean = true;
    pagingNoTouching: boolean = true;
    showItem: boolean = false;
    @ViewChildren(ItemSummary, { read: ElementRef }) itemSummaries: QueryList<ElementRef>;

    constructor(private router: Router,
                private route: ActivatedRoute,
                dialog: MatDialog,
                private itemService: ItemService,
                private storageSerice: StorageService,
                private tenantService: TenantService,
                private userService: UserService){
        router.events
        .pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this.cleanupTime)
        )
        .subscribe(() => {
            this.userService.isLoggedIn()
            .pipe(
                map(status => status.user && !_.find(status.user.tenantRoles, { roleType: 'TENANT_ADMIN' }) ? true : false),
                filter(isNotAdmin => isNotAdmin),
                flatMap(() => this.tenantService.getUnauthTenantInfo()),
                filter(tenant => ['TRIAL_EXPIRED', 'UNPAID'].includes(tenant.status))
            )
            .subscribe((tenant) => {
                dialog.open(TenantSuspendedDialogComponent, { data: tenant.status === 'UNPAID', disableClose: true });
            });

            this.showItem = !!this.route.firstChild;
            if (!this.showItem) {
                setTimeout(() => {
                    const targetItem = this.storageSerice.retrieve('recent-item-clicked');
                    if (targetItem && this.itemSummaries) {
                        const targetSummary = this.itemSummaries.find((itemSummary: ElementRef) => itemSummary.nativeElement.getAttribute('itemId') === targetItem);
                        if(targetSummary) {
                            targetSummary.nativeElement.scrollIntoView();
                        }
                    }
                });
            } else {
                this.storageSerice.store('recent-item-clicked', this.route.firstChild.snapshot.params.itemId);
            }
        });
        
        this.searchParams
        .pipe(
            filter(params => !!params),
            tap(() => this.pagingNoTouching = true),
            flatMap(params => this.userService.searchItems(params)),
            takeUntil(this.cleanupTime)
        )
        .subscribe(itemResults => {
            this.totalItems = itemResults.totalElements;
            itemResults.content.forEach(item => item.visible = true);
            this.items.next(this.items.value.concat(itemResults.content));
            this.noTouching = false;
            this.pagingNoTouching = false;
        });
    }

    ngOnInit() {
        this.route.url.pipe(takeUntil(this.cleanupTime)).subscribe((segs: UrlSegment[]) => {
            if (segs.length) {
                this.itemService.focusedItemEdit.next(undefined);
                const itemId = segs[1].path;
                let item = this.items.value.find(item => item.id === itemId);
                const isSource = false; 
                const curating = false;
                if (!item) {
                    this.itemService.getItem(itemId)
                    .pipe(map(response => response.item))
                    .subscribe(fetchedItem => this.itemService.focusedItem.next(new ItemContextModel(fetchedItem, undefined, undefined, undefined, isSource, curating)));
                } else {
                    this.itemService.focusedItem.next(new ItemContextModel(item, undefined, undefined, undefined, isSource, curating));
                }
            } else {
                this.itemService.focusedItem.next(undefined);
            }
        });
        const baseParams = { page: 0, includeFullDescription: true, sortField: 'publishedDate', includeReadItems: false, createdAfterDateTime: this.dateBound() };
        this.itemService.itemClosing.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.router.navigateByUrl(this.itemService.childClearedPath()));
        this.itemService.itemContextChanged.pipe(takeUntil(this.cleanupTime)).subscribe(() => this.searchParams.next(baseParams));
        this.storageSerice.store('recent-item-clicked', null);
        this.searchParams.next(baseParams);
    }

    dateBound = () => {
        const user = this.userService.getCurrentUser();
        const userVal = user.context ? user.context.recentItemCreatedAfterDate : '';
        const bound = _.isEmpty(userVal) ? moment().subtract(2, 'days') : moment(userVal) 
        const defaultBound = moment().subtract(2, 'days');
        return ((!bound.isValid() || bound.isBefore(defaultBound)) ? defaultBound : bound).toISOString();
    };

    sort(sortField?) {
        this.noTouching = true;
        this.items.next([]);
        this.searchParams.next(Object.assign({}, this.searchParams.value, { sortField, page: 0 }));
    }

    retrievePage = () => {
        this.searchParams.next(Object.assign({}, this.searchParams.value, { page: ++this.searchParams.value.page }));
    }

    markNow() {
        const user = this.userService.getCurrentUser();
        user.context = user.context || {};
        user.context['recentItemCreatedAfterDate'] = moment().toISOString();
        this.userService.updateCurrentUser(user).subscribe();
        this.totalItems = undefined;
        this.items.next([]);
        this.searchParams.next(Object.assign({}, this.searchParams.value, { page: 0, createdAfterDateTime: this.dateBound() }));
    };

    ngOnDestroy() {
        this.cleanupTime.next();
        this.cleanupTime.complete();
        this.searchParams.complete();
        this.items.complete();
    };
}
